import { useCallback, useSyncExternalStore } from 'react';
import {
  getVendorColorOverrides,
  subscribeVendorColorOverrides,
  setVendorColorOverride,
  resetVendorColorOverride,
  resolveVendorColor,
} from '../../../lib/vendorColorOverrides';

/**
 * React hook that exposes the live vendor color override map plus helpers to
 * resolve, edit, and reset a vendor's color. Uses `useSyncExternalStore` so
 * every subscriber re-renders when any override changes (including from
 * other tabs via the `storage` event).
 */
export function useVendorColors() {
  const overrides = useSyncExternalStore(
    subscribeVendorColorOverrides,
    getVendorColorOverrides,
    getVendorColorOverrides
  );

  // `overrides` is the reactive dep — resolveVendorColor reads the live map
  // on each call, but we want consumers to re-render when it changes.
  const colorFor = useCallback(
    (vendor) => resolveVendorColor(vendor),
    [overrides]
  );

  const hasOverride = useCallback(
    (vendor) => vendor != null && overrides[String(vendor.id)] != null,
    [overrides]
  );

  const setColor = useCallback((vendor, hex) => {
    if (!vendor) return;
    setVendorColorOverride(vendor.id, hex);
  }, []);

  const resetColor = useCallback((vendor) => {
    if (!vendor) return;
    resetVendorColorOverride(vendor.id);
  }, []);

  return { overrides, colorFor, hasOverride, setColor, resetColor };
}
